import { RefObject, useEffect, useRef } from 'react';
import { createPopper } from '@popperjs/core';
import { OptionItem } from './option-item';
import { ObjectPillListItem } from '../pill-list';

export interface MultiselectWithSearchDropdownContentProps<A, T> {
  setIsExpanded: (isExpanded: boolean) => void;
  buttonRef: RefObject<HTMLButtonElement | HTMLInputElement | null>;
  selected: A[];
  handleSelect: (option?: ObjectPillListItem) => void;
  addEmptyOption: boolean;
  options: T[];
  mapOptions: (option: T) => ObjectPillListItem;
}

export const MultiselectWithSearchDropdownContent = <A, T>({
  setIsExpanded,
  buttonRef,
  selected,
  handleSelect,
  addEmptyOption,
  options,
  mapOptions,
}: MultiselectWithSearchDropdownContentProps<A, T>) => {
  const dropdownRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (buttonRef?.current && dropdownRef.current) {
      createPopper(buttonRef.current, dropdownRef.current, {
        placement: 'bottom-start',
      });
    }
  }, [buttonRef, dropdownRef]);

  return (
    <ul
      ref={dropdownRef}
      className="absolute z-50 mt-1 max-h-60 w-fit overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
      tabIndex={-1}
      role="listbox"
      aria-labelledby="listbox-label"
      aria-activedescendant="listbox-option-3"
    >
      {addEmptyOption && (
        <OptionItem
          key=""
          isSelected={selected.length === 0}
          onClick={() => handleSelect()}
          label="None"
        />
      )}
      {options?.map((singleOption: T) => {
        const option = mapOptions?.(singleOption) ?? singleOption;
        return (
          <OptionItem
            key={option.key}
            isSelected={selected.some((selectedItem: any) => {
              return selectedItem.key === option.key;
            })}
            onClick={() => {
              handleSelect(option);
            }}
            label={option.label}
          />
        );
      })}
    </ul>
  );
};

export default MultiselectWithSearchDropdownContent;
